import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import type { ShiritoriGameStore } from '@/game/core/game.store';
import type { Player } from '@/game/core/player';
import { Ellipsis } from '../ellipsis';
import { Card } from '../home/card';
import { useTranslation } from 'react-i18next';

interface SpectatePanelProps {
    game: ShiritoriGameStore;
}

export const SpectatePanel = observer(function ({ game }: SpectatePanelProps) {
    const { t } = useTranslation();
    const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(
        game.currentTurnRemainingSeconds()
    );
    const isTimerActive = game.isTimerActive;
    const turnTimerKey = game.turnTimerKey;

    useEffect(() => {
        if (!isTimerActive) {
            setRemainingSeconds(undefined);
            return;
        }

        const tick = () => {
            const remaining = game.currentTurnRemainingSeconds();
            setRemainingSeconds(remaining);

            if (remaining !== undefined && remaining <= 0) {
                game.expireTurnIfNeeded();
            }
        };

        tick();
        const intervalId = setInterval(tick, 1000);

        return () => clearInterval(intervalId);
    }, [game, isTimerActive, turnTimerKey]);

    const players = game.turnSortedPlayers;
    const currentPlayer = game.nextToPlay();

    if (players.length === 0) {
        return null;
    }

    return (
        <div className="fixed left-0 bottom-0 w-screen flex justify-center mb-10">
            <Card className="h-full w-lg max-w-2xl">
                <div className="mb-4">
                    <p className="text-xs tracking-wide opacity-60">{t('game.gameplay.spectate.waitingOn', { player: currentPlayer.name })}<Ellipsis /></p>
                    {remainingSeconds !== undefined && (
                        <p className="text-xs font-semibold text-game-highlight mt-1">{t('game.gameplay.spectate.timeLeft', { seconds: remainingSeconds })}</p>
                    )}
                </div>

                <div className={`w-full grid grid-cols-${players.length} gap-2 sm:gap-4`}>
                    {players.map((player, index) => <PlayerChip key={player.id} player={player} currentPlayerId={currentPlayer.id} />)}
                </div>
            </Card>
        </div>
    );
});

interface PlayerChipProps {
    player: Player;
    currentPlayerId: string;
}

function PlayerChip({ player, currentPlayerId }: PlayerChipProps) {
    const isCurrent = player.id === currentPlayerId;

    const circleColor = ['black', 'white'].includes(player.team as string)
        ? `bg-${player.team}`
        : `bg-${player.team}-400`;

    const circleRing = isCurrent
        ? 'ring-4 ring-primary scale-110'
        : 'ring-2 ring-primary/50';

    const nameStyles = isCurrent
        ? 'bg-secondary text-background'
        : 'text-secondary';

    const whiteBorder = player.team === 'white' ? 'border border-secondary' : '';

    return (
        <div className="flex flex-col items-center">
            <div className={`m-2 h-6 w-6 rounded-full ${circleColor} ${whiteBorder} ${circleRing} transition duration-200`} />
            <div className={`text-xs sm:text-sm text-center px-2 py-1 rounded-sm ${nameStyles}`}>{player.name}</div>
        </div>
    );
}
